import * as turf from '@turf/turf';
import { FeatureCollection, Feature, Polygon, MultiPolygon } from 'geojson';

/**
 * Clip features by bounding box
 */
export function clipByBBox(
  geojson: FeatureCollection,
  bbox: [number, number, number, number] // [minX, minY, maxX, maxY]
): FeatureCollection {
  const clipped: Feature[] = [];
  const bboxPolygon = turf.bboxPolygon(bbox);
  
  geojson.features.forEach((feature) => {
    try {
      // Check if feature intersects with bbox
      if (turf.booleanIntersects(feature as any, bboxPolygon)) {
        // Clip the feature
        const result = clipFeature(feature, bboxPolygon.geometry);
        if (result) {
          clipped.push(result);
        }
      }
    } catch (error) {
      // Skip features outside clip extent
    }
  });
  
  return {
    type: 'FeatureCollection',
    features: clipped,
    ...(geojson as any).crs && { crs: (geojson as any).crs }
  } as FeatureCollection;
}

/**
 * Clip features by polygon
 */
export function clipByPolygon(
  geojson: FeatureCollection,
  clipPolygon: Feature<Polygon | MultiPolygon> | Polygon | MultiPolygon
): FeatureCollection {
  const clipped: Feature[] = [];
  const polygon = (clipPolygon.type === 'Feature'
    ? (clipPolygon as Feature<Polygon | MultiPolygon>).geometry
    : clipPolygon) as Polygon;
  
  geojson.features.forEach((feature) => {
    try {
      // Check if feature intersects with clip polygon
      if (turf.booleanIntersects(feature as any, turf.feature(polygon) as any)) {
        const result = clipFeature(feature, polygon);
        if (result) {
          clipped.push(result);
        }
      }
    } catch (error) {
      // Skip features outside clip extent
    }
  });
  
  return {
    type: 'FeatureCollection',
    features: clipped,
    ...(geojson as any).crs && { crs: (geojson as any).crs }
  } as FeatureCollection;
}

/**
 * Clip a single feature by polygon
 */
function clipFeature(feature: Feature, clipPolygon: Polygon): Feature | null {
  try {
    const geomType = feature.geometry.type;
    
    // For points, check if inside
    if (geomType === 'Point' || geomType === 'MultiPoint') {
      const isInside = turf.booleanPointInPolygon(feature as any, turf.feature(clipPolygon) as any);
      return isInside ? feature : null;
    }
    
    // For lines and polygons, use intersection
    if (geomType === 'LineString' || geomType === 'MultiLineString' || 
        geomType === 'Polygon' || geomType === 'MultiPolygon') {
      const intersection = turf.intersect(
        turf.featureCollection([feature, turf.feature(clipPolygon)]) as any
      );
      
      if (intersection) {
        return {
          ...intersection,
          properties: feature.properties
        };
      }
    }
    
    return null;
  } catch (error) {
    // Return empty collection on clip failure
    return null;
  }
}

/**
 * Parse bbox string (e.g., "minX,minY,maxX,maxY")
 */
export function parseBBox(bboxString: string): [number, number, number, number] | null {
  const parts = bboxString.split(',').map(s => parseFloat(s.trim()));
  
  if (parts.length !== 4 || parts.some(isNaN)) {
    return null;
  }
  
  const [minX, minY, maxX, maxY] = parts;
  
  // Validate bbox
  if (minX >= maxX || minY >= maxY) {
    return null;
  }
  
  return [minX, minY, maxX, maxY];
}

/**
 * Get bbox from features
 */
export function getBBoxFromFeatures(geojson: FeatureCollection): [number, number, number, number] {
  return turf.bbox(geojson) as [number, number, number, number];
}
